"use client"

import { useEffect, useState } from "react"

/**
 * FlipBoard - a split-flap clock on the EmpCo application date.
 *
 * It counted down to 27 September 2026 and now counts up from it. The deadline
 * passing did not make the board pointless, it changed what it argues: not
 * "this is coming" but "this has been law for N days and your site still says
 * it". So the date is fixed and the direction flipped, rather than the board
 * being retired.
 *
 * The board starts at zero and is filled in on mount, never during render:
 * /v2 is statically prerendered, so a figure worked out at build time would
 * freeze on the day the build ran, and reading the clock while rendering would
 * make the server and the client disagree. Starting at zero also means the
 * first tick flips every digit into place, which is the effect anyway.
 */

/** EmpCo has applied since 27 September 2026. */
const APPLIED = Date.UTC(2026, 8, 27)

type Elapsed = { days: string; hrs: string; mins: string; secs: string }

const ZERO: Elapsed = { days: "00", hrs: "00", mins: "00", secs: "00" }

function elapsed(now: number): Elapsed {
  // Clamped, so a clock set behind the date reads zero rather than counting
  // backwards through a negative.
  const secs = Math.max(0, Math.floor((now - APPLIED) / 1000))
  const pad = (n: number) => String(n).padStart(2, "0")
  return {
    // Days are padded to two but not capped at them: past 99 the group grows a
    // third card, which is the honest reading and what the board is for.
    days: pad(Math.floor(secs / 86_400)),
    hrs: pad(Math.floor(secs / 3_600) % 24),
    mins: pad(Math.floor(secs / 60) % 60),
    secs: pad(secs % 60)
  }
}

/** One card. Keeps the digit it is leaving so the leaves have something to
 * show, and bumps a sequence so the animation restarts on every change. */
function FlipDigit({ value }: { value: string }) {
  const [state, setState] = useState({ shown: value, was: value, seq: 0 })

  // Deriving from props during render - React re-runs this pass immediately,
  // so the leaves and the static halves are always drawn from the same tick.
  if (state.shown !== value) {
    setState((s) => ({ shown: value, was: s.shown, seq: s.seq + 1 }))
  }

  return (
    <span className="v2-flip-card">
      <span className="v2-flip-half is-top">
        <span>{state.shown}</span>
      </span>
      <span className="v2-flip-half is-bottom">
        <span>{state.was}</span>
      </span>

      {state.seq > 0 && (
        <span key={state.seq} className="contents">
          <span className="v2-flip-half v2-flip-leaf is-top">
            <span>{state.was}</span>
          </span>
          <span className="v2-flip-half v2-flip-leaf is-bottom">
            <span>{state.shown}</span>
          </span>
        </span>
      )}
    </span>
  )
}

function Group({
  value,
  label,
  onDark
}: {
  value: string
  label: string
  onDark?: boolean
}) {
  return (
    <div>
      <div className="flex gap-1.5">
        {/* Position IS the identity: a card is a fixed slot on the board, so
            the key is the slot, not the digit that happens to be in it. */}
        {value.split("").map((digit, slot) => (
          <FlipDigit key={`${label}-${slot}`} value={digit} />
        ))}
      </div>
      <p
        className={`mt-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] [.is-inverted_&]:text-white/70 ${
          onDark ? "text-white/70" : "text-(--v2-accent)/60"
        }`}
      >
        {label}
      </p>
    </div>
  )
}

export function FlipBoard({
  /** White cards with ink digits, for a board on a dark panel. The default is
   * ink cards, which is right on the light page and invisible on ink. */
  inverted = false,
  /** The unit labels read on a dark ground.
   *
   * Separate from `inverted` because the two answer different questions: that
   * one is what colour the CARDS are, this is what is BEHIND them. On a
   * saturated field the ink cards are right and the labels still have to
   * lift, and tying the two together left DAYS / HOURS / MINUTES / SECONDS
   * near-invisible on the blue. */
  onDark = false
}: {
  inverted?: boolean
  onDark?: boolean
} = {}) {
  const [value, setValue] = useState<Elapsed>(ZERO)

  useEffect(() => {
    const tick = () => setValue(elapsed(Date.now()))
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div
      // Wraps rather than scrolls: on a phone the four groups fold to
      // two-by-two instead of running off the side.
      className={`v2-flip flex flex-wrap justify-center gap-x-5 gap-y-6 text-3xl font-semibold md:gap-x-6 md:text-5xl ${
        inverted ? "is-inverted" : ""
      }`}
      // The board ticks every second; a screen reader should not have to hear
      // it. The date it counts from is stated in the copy beside it.
      aria-hidden
    >
      <Group value={value.days} label="Days" onDark={onDark} />
      <Group value={value.hrs} label="Hours" onDark={onDark} />
      <Group value={value.mins} label="Minutes" onDark={onDark} />
      <Group value={value.secs} label="Seconds" onDark={onDark} />
    </div>
  )
}
